// Core reducers — needed for login/app shell
import app from './app';
import account from './account';
import main from './main';
import ezlo from './ezlo';
import ezloCustomization from './ezloCustomization';
import oem from './ezloInitialization';
import customizationData from './customizationData';
import ezloPolicy from '../features/EzloPolicies/reducers/EzloPoliciesReducer';
import modals from './modals';
import toasts from './toasts';
import navigation from './navigation';
import { accountApi, accountApiReducer } from '../api/accountApi';

export const coreReducers = {
    app,
    account,
    [accountApi.reducerPath]: accountApiReducer,
    main,
    ezlo,
    oem,
    modals,
    toasts: toasts.reducer,
    navigation,
    ezloCustomization,
    customizationData,
    ezloPolicy,
};
const defaultExtract = (m) => m.default;
// Single source of truth for lazy reducers.
// Each entry: [key, importFn] or [key, importFn, extractFn] for non-default exports
// Adding a new reducer requires only one line here.
// Note: explicit import() calls are required for webpack code-splitting to work correctly.
const lazyReducerDefinitions = [
    ['ezlos', () => import('./ezlos')],
    ['gateways', () => import('./gateways')],
    ['groups', () => import('./groups')],
    ['ezloRules', () => import('./reducerRules')],
    ['backups', () => import('./backups')],
    ['virtualDeviceManager', () => import('./virtualDeviceManager')],
    ['meshBot', () => import('./meshBot')],
    ['notifications', () => import('./notifications')],
    ['plugins', () => import('./plugins')],
    ['tokensFlows', () => import('../features/tokensFlows/reducer')],
    ['users', () => import('./users')],
    ['integrations', () => import('./integrations')],
    ['deviceSettings', () => import('./deviceSettings')],
    ['mediaStore', () => import('../features/MediaStoragePage/reducer/media')],
    ['migration', () => import('../features/Migration/reducer/migration')],
    ['geofenceSettings', () => import('./geofenceSettings')],
    ['supportData', () => import('./ezloSupport')],
    ['subscriptions', () => import('./subscriptions')],
    ['deviceGroups', () => import('../containers/Ezlo/EzloDeviceGroups/reducer/deviceGroups')],
    ['meshBotV2', () => import('./meshBotV2')],
    ['kvs', () => import('./kvs')],
    ['labels', () => import('./labels')],
    ['alert/history', () => import('./alertHistory')],
    ['trackRunScenes', () => import('./trackRunScenes')],
    ['staticData', () => import('./staticData')],
    ['installerAccessStore', () => import('../features/InstallerAccess/reducers/installerAccessRights')],
    ['duplicateMeshBotStore', () => import('containers/Ezlo/EzloMeshbots/reducers/meshbotDuplicate')],
    ['meshBotsFiltersStore', () => import('./meshBotsFilters')],
];

// Generate lazyReducerImports from definitions
// Used in production for code-splitting
export const lazyReducerImports = Object.fromEntries(
    lazyReducerDefinitions.map(([key, importFn, extractFn = defaultExtract]) => [
        key,
        () => importFn().then(extractFn),
    ]),
);

// Extracts module path from import/require function for tests
// Babel transforms import() to require() in test environment
function getPathFromImportFn(importFn) {
    const fnString = importFn.toString();
    // Match both: import('./path') and require('./path')
    const match = fnString.match(/(?:import|require)\s*\(\s*['"]([^'"]+)['"]\s*\)/);
    if (!match) {
        throw new Error(`Cannot extract path from: ${fnString}`);
    }

    return match[1];
}

// Returns all reducers (core + lazy) synchronously. Used in test environment
// where async import() causes race conditions with preloadedState.
// In production, the process.env.NODE_ENV check is replaced by DefinePlugin,
// so the require() calls are dead-code-eliminated from the bundle.
export function getAllReducers() {
    if (process.env.NODE_ENV !== 'test') {
        return coreReducers;
    }

    const lazyReducers = Object.fromEntries(
        lazyReducerDefinitions.map(([key, importFn, extractFn = defaultExtract]) => {
            const path = getPathFromImportFn(importFn);
            const module = require(`${path}`);

            return [key, extractFn(module)];
        }),
    );

    return { ...coreReducers, ...lazyReducers };
}
